// ABOUTME: Renders the public Better Email homepage as React components.
// ABOUTME: Connects the launch special intake, billing, CRM connection, and success flow to Convex and Stripe.
"use client";

import "./landing-page.css";
import "@/app/globals-v2.css";
import CurvedArrow from "@/public/images/CurvedArrow";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { useAuthActions } from "@convex-dev/auth/react";
import parsePhoneNumber from "libphonenumber-js";

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Link from "next/link";
import { api } from "@/convex/_generated/api";
import { useBetterEmailAuth } from "@/providers/better-email-auth-provider";
import { cn } from "@/lib/utils";
import { getCrmConfig, getCrmName, supportedCrmSlugs } from "@/config/crm-logos";
import { ConnectCrmButtons } from "@/app/v2/_components/contact-list/connect-crm-buttons";
import BetterEmailLogo from "@/components/logos/better-email-logo";
import { Button } from "./v2/_components/button/button";
import ShinyText from "@/src/TextAnimations/ShinyText/ShinyText";
import { Row } from "./v2/_components/row/Row";
import { Column } from "./v2/_components/column/Column";
import { Text } from "./v2/_components/text/Text";
import { AlertCircle, ArrowBigDown, ArrowDown, ArrowLeft, ArrowRight, Check, Loader2, Lock, LockKeyhole, Shield, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { Id } from "@/convex/_generated/dataModel";
import { SupportOutreach } from "./v2/_components/support-outreach/SupportOutreach";
import Image from "next/image";

const getStartedApi = api.getStarted;
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);
const LANDING_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Patrick+Hand&display=swap";

const stats = [
  { value: 1000, suffix: "+", label: "Agents enrolled" },
  { value: 98, suffix: "%", label: "Avg inbox rate" },
  { value: 17, suffix: "", label: "CRM integrations" },
  { value: 7, suffix: " days", label: "To first send" },
];

const comparisons = [
  "You write the emails",
  "You manage deliverability",
  "You segment the list",
  "You forget to send for 3 weeks",
];

const betterEmailComparison = [
  "We write the emails",
  "We manage deliverability",
  "We segment the list",
  "You answer the call when it rings",
];

const chapters = [
  {
    number: "01",
    title: "Database enrichment",
    kicker: "Know who you're talking to",
    headline: "You know you should email your database. You just don't know",
    highlight: "what to say to each person.",
    note:
      "Past clients start replying. Not because you got lucky — because the email felt like it came from someone paying attention.",
    cta: "Get started",
    mockup: "contacts",
    tilt: "left",
    items: [
      {
        title: "That's why we built database enrichment.",
        body:
          "A generic email to someone who knows you feels wrong. So you don't send it. We find the home value, equity, and ownership length for every contact. Now every email has a real reason to exist.",
      },
      {
        title: "Smart segmentation",
        body: "We sort your contacts into groups so each person gets something that makes sense for them. Past clients, sphere, active leads.",
      },
      {
        title: "17 CRM integrations",
        body: "kvCORE, Follow Up Boss, LionDesk, Sierra, Chime, BoomTown, and 11 more. We connect directly to yours.",
      },
    ],
  },
  {
    number: "02",
    title: "AI writing safeguards",
    kicker: "Emails that sound like you",
    headline: "You've seen what bad AI emails look like. You swore you'd",
    highlight: "never send one.",
    note: 'You stop worrying about what went out. You start getting replies that say "I was just thinking about you."',
    cta: "Get started",
    mockup: "preview",
    tilt: "right",
    items: [
      {
        title: "That's why we built guardrails into every send.",
        body:
          "You have a reputation. One bad email can undo years of trust. Before anything goes out, we check the tone, verify the data, and run it through compliance.",
      },
      {
        title: "Latest LLMs",
        body:
          "Every email uses the best AI available, plus real data from your database — so it reads like something you'd actually send.",
      },
      {
        title: "Human oversight",
        body: "Our team watches every send. The AI writes it. We make sure it's right. You never worry about what went out.",
      },
    ],
  },
  {
    number: "03",
    title: "Deliverability management",
    kicker: "Emails that actually land",
    headline: "Your emails were going to spam",
    highlight: "and you had no idea.",
    note: "98% of your emails land in the inbox. Not the promotions tab. The inbox — where people actually read.",
    cta: "Get started",
    mockup: "deliverability",
    tilt: "left",
    items: [
      {
        title: "That's why we manage your deliverability from day one.",
        body:
          "Most agents who quit email didn't run out of things to say. They quit because their sending domain burned out quietly. Email was working — it just wasn't arriving.",
      },
      {
        title: "IP warming, DMARC, SPF, DKIM",
        body:
          "We set all of this up before your first send and watch it every day. You never find out months later that your emails have been going to spam.",
      },
      {
        title: "List cleansing",
        body: "We remove bad addresses before they hurt your sender score. Your bounce rate stays low.",
      },
    ],
  },
  {
    number: "04",
    title: "White-glove onboarding",
    kicker: "One call and you're live",
    headline: "You don't need another tool to learn. You need someone",
    highlight: "to just do it.",
    note:
      'You get off the call and emails are already going out. Next month you get a call from someone who says "I was just thinking about you."',
    cta: "Get started",
    mockup: "onboarding",
    tilt: "right",
    items: [
      {
        title: "That's why we do the whole setup for you.",
        body:
          "You've tried the platforms. You set things up, sent a few emails, missed a week, missed a month, and stopped. The tool didn't fail you — it asked you to become an email marketer on top of being an agent.",
      },
      {
        title: "One call. We handle the rest.",
        body: "We connect your CRM, pull your contacts, build your groups, schedule your first emails. You show up. We do the work.",
      },
      {
        title: "Your own onboarding specialist",
        body: "A real person on our team handles your setup live on the call. They do the work while you watch. No prep needed.",
      },
    ],
  },
];

const timelineDays = [
  {
    day: "Day 1",
    title: "The call",
    body: "A 30-minute onboarding call with your onboarding specialist. They connect your CRM live on the call. You watch.",
  },
  {
    day: "Day 3",
    title: "The enrichment",
    body: "We attach home value, equity, and ownership data to every contact. Your database now knows things you didn't.",
  },
  {
    day: "Day 7",
    title: "First email goes out",
    body: "Your first personalized email is written, reviewed, and sent. From here, every week — without you lifting a finger.",
  },
];

const emailExamples = [
  {
    note: "to: Mike R.",
    title: "Your home is up $94k since '18",
    body: '"Was looking at recent sales on Maple St. and thought you\'d want to see what\'s happening..."',
    tag: "home value alert",
  },
  {
    note: "to: Sarah L.",
    title: "3 sellers on your block this month",
    body: '"Something\'s happening in your neighborhood. Wanted to share what I\'m seeing before you hear it from a neighbor..."',
    tag: "neighborhood alert",
  },
  {
    note: "to: James K.",
    title: "A house just listed two doors down",
    body: '"Quick heads up — 247 Oak just hit the market at $1.2M. Curious what you think since you know the area..."',
    tag: "just listed",
  },
];

const objections = [
  {
    question: "But will it sound like me?",
    arrowPath: "M 30 5 Q 25 25 30 45",
    title: "Yes — we train on your voice.",
    body:
      "On your onboarding call we record how you actually talk. The AI is prompted to match it. Plus our team reviews every send.",
  },
  {
    question: "What if my database is messy?",
    arrowPath: "M 30 5 Q 35 25 30 45",
    title: "Good. That's what we do.",
    body:
      "We clean it, dedupe it, fill in missing data, and segment it. Most agents are shocked by how many people they forgot they had.",
  },
  {
    question: "What about CAN-SPAM and compliance?",
    arrowPath: "M 30 5 Q 27 25 30 45",
    title: "All of it. Handled.",
    body:
      "Unsubscribe links, physical address, sender authentication — all set up before your first email goes out. You don't learn any of it.",
  },
];

const cardElementStyle = {
  base: {
    fontSize: "15px",
    lineHeight: "24px",
    color: "#000",
    fontFamily: "Inter, system-ui, sans-serif",
    "::placeholder": {
      color: "#AAA",
    },
  },
  invalid: {
    color: "#A84030",
    iconColor: "#A84030",
  },
};

type FlowStep = "billing" | "connect" | "intake" | "success";

interface PrepareBillingMetadata {
  billingAlreadyActive: boolean;
  clientSecret: string | null;
  customerId: string | null;
}

interface CompleteBillingMetadata {
  billingAlreadyActive: boolean;
  status: string | null;
  subscriptionId: string | null;
}

type BillingMetadata = CompleteBillingMetadata | PrepareBillingMetadata;

type LeadFormState = {
  name: string;
  phone: string;
  email: string;
  crm: string;
};

type FlowState = {
  leadId: Id<"salesLeads">;
  teamId: Id<"teams">;
  userId: Id<"users">;
  token: string;
  authToken: string;
  billingMetadata: BillingMetadata | null;
};

type CompleteBilling = (args: {
  leadId: Id<"salesLeads">;
  token: string;
  paymentMethodId: string;
}) => Promise<CompleteBillingMetadata>;
type HistoryUpdateMode = "push" | "replace";
interface SignupUrlState {
  step: FlowStep;
  crm: string | null;
}

const SIGNUP_STORAGE_KEY = "better-email.signup-flow.v1";
const DEFAULT_LEAD_FORM: LeadFormState = {
  name: "",
  phone: "",
  email: "",
  crm: "",
};
const flowStepToUrlValue: Record<FlowStep, string> = {
  intake: "name",
  billing: "billing",
  connect: "connect",
  success: "success",
};
const urlValueToFlowStep: Partial<Record<string, FlowStep>> = {
  name: "intake",
  billing: "billing",
  connect: "connect",
  success: "success",
};

type StoredSignupState = {
  form: LeadFormState;
  flowState: FlowState | null;
};

function getErrorMessage(caughtError: unknown, fallback: string) {
  return caughtError instanceof Error && caughtError.message ? caughtError.message : fallback;
}

function isPrepareBillingMetadata(metadata: BillingMetadata | null | undefined): metadata is PrepareBillingMetadata {
  return Boolean(metadata && "clientSecret" in metadata);
}

function isCompleteBillingMetadata(metadata: BillingMetadata | null | undefined): metadata is CompleteBillingMetadata {
  return Boolean(metadata && "status" in metadata);
}

function getBillingClientSecret(metadata: BillingMetadata | null | undefined) {
  return isPrepareBillingMetadata(metadata) ? metadata.clientSecret : null;
}

function isBillingComplete(metadata: BillingMetadata | null | undefined) {
  return (
    metadata?.billingAlreadyActive === true ||
    (isCompleteBillingMetadata(metadata) && (metadata.status === "active" || metadata.status === "trialing"))
  );
}

function getInactiveBillingMessage(metadata: BillingMetadata | null | undefined) {
  if (!isCompleteBillingMetadata(metadata) || isBillingComplete(metadata)) {
    return null;
  }

  const statusLabel = metadata.status ? ` Stripe returned "${metadata.status}".` : "";
  return `We could not activate billing yet.${statusLabel} Please try again or contact support if this keeps happening.`;
}

function formatPhoneInput(value: string) {
  if (!value) return value;
  try {
    return parsePhoneNumber(value, { defaultCountry: "US" })?.formatNational() ?? value;
  } catch {
    return value;
  }
}

function hasLeadFormValue(form: LeadFormState) {
  return Boolean(form.name || form.phone || form.email || form.crm);
}

function readSignupStorage(): StoredSignupState | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(SIGNUP_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredSignupState>;
    return {
      form: {
        ...DEFAULT_LEAD_FORM,
        ...(parsed.form ?? {}),
      },
      flowState: parsed.flowState ?? null,
    };
  } catch {
    return null;
  }
}

function writeSignupStorage(form: LeadFormState, flowState: FlowState | null) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      SIGNUP_STORAGE_KEY,
      JSON.stringify({
        form,
        flowState,
      }),
    );
  } catch {
    return;
  }
}

function normalizeCrmSlugFromUrl(value: string | undefined) {
  if (!value) {
    return null;
  }

  try {
    const slug = getCrmConfig(decodeURIComponent(value).trim().toLowerCase())?.slug;
    return slug && supportedCrmSlugs.includes(slug) ? slug : null;
  } catch {
    const slug = getCrmConfig(value.trim().toLowerCase())?.slug;
    return slug && supportedCrmSlugs.includes(slug) ? slug : null;
  }
}

function getSignupStateFromUrl(): SignupUrlState | null {
  if (typeof window === "undefined") {
    return null;
  }

  const signupPathMatch = /^\/signup\/([^/]+)(?:\/([^/]+))?\/?$/.exec(window.location.pathname);
  if (signupPathMatch?.[1]) {
    const step = urlValueToFlowStep[signupPathMatch[1]];
    if (!step) {
      return null;
    }

    return {
      step,
      crm: step === "connect" ? normalizeCrmSlugFromUrl(signupPathMatch[2]) : null,
    };
  }

  const rawStep = new URLSearchParams(window.location.search).get("signup");
  if (rawStep) {
    const step = urlValueToFlowStep[rawStep];
    return step ? { step, crm: null } : null;
  }

  return null;
}

function getSignupStepFromUrl() {
  return getSignupStateFromUrl()?.step ?? null;
}

function getSignupPath(step: FlowStep) {
  return `/signup/${flowStepToUrlValue[step]}`;
}

function getCurrentSignupRedirectPath() {
  if (typeof window === "undefined") {
    return getSignupPath("intake");
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (path.startsWith("/signup/") || path.startsWith("/?signup=")) {
    return path;
  }

  return getSignupPath(getSignupStepFromUrl() ?? "intake");
}

function updateSignupUrl(step: FlowStep, mode: HistoryUpdateMode) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  const nextUrl = `${getSignupPath(step)}${url.hash}`;
  if (mode === "replace") {
    window.history.replaceState(null, "", nextUrl);
    return;
  }

  window.history.pushState(null, "", nextUrl);
}

function clearSignupUrl(mode: HistoryUpdateMode) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  const nextUrl = `/${url.hash}`;
  if (mode === "replace") {
    window.history.replaceState(null, "", nextUrl);
    return;
  }

  window.history.pushState(null, "", nextUrl);
}

function getSecureCrmHref(crm: string) {
  if (!crm) {
    return "/secure";
  }

  const crmName = getCrmName(crm);
  return `/secure?crm=${encodeURIComponent(crmName)}`;
}

function scrollToTop() {
  if (typeof window === "undefined" || window.navigator.userAgent.includes("jsdom")) {
    return;
  }

  window.scrollTo(0, 0);
}

function PrimaryCta({
  children = "Get Launch Special",
  onClick,
  className,
}: {
  children?: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      onClick={onClick}
      shineColorRgba="#ffffff22"
      animationDuration={3}
      className={cn(
        "inline-flex items-center justify-center gap-[10px] rounded-[6px] bg-[#3A3530] px-[22px] py-[14px] text-[15px] font-bold leading-none tracking-[-.005em] text-[#FCFAF4] shadow-xs hover:bg-[#2f2b27] active:bg-[#2f2b27] md:px-[30px] md:py-[16px] md:text-[16px]",
        className,
      )}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" className="shrink-0">
        <path d="M9 1.5l1.85 4.4 4.65.45-3.5 3.1 1 4.55L9 11.6l-4 2.4 1-4.55L2.5 6.35l4.65-.45L9 1.5z" fill="currentColor" />
      </svg>
      {children}
    </Button>
  );
}

function SecondaryCta() {
  return (
    <a
      href="#video"
      className="inline-flex w-full items-center justify-center gap-[10px] rounded-[6px] border-[1.5px] border-[#3A3530] px-[22px] py-[14px] text-[15px] font-semibold leading-none tracking-[-.005em] text-[#3A3530] shadow-[2px_3px_0_rgba(58,53,48,.08)] transition hover:-translate-y-px hover:bg-[#F7F3E8] md:w-auto md:px-[28px] md:py-[15px] md:text-[16px]"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" className="shrink-0">
        <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7.5 6.2l4.3 2.8-4.3 2.8V6.2z" fill="currentColor" />
      </svg>
      Watch 5-min Product Video
    </a>
  );
}

function useLandingEffects(rootRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    const revealElements = root.querySelectorAll("[data-reveal]");
    const statElements = root.querySelectorAll<HTMLElement>("[data-stat-target]");

    if (!("IntersectionObserver" in window)) {
      revealElements.forEach((element) => element.classList.add("in"));
      statElements.forEach((element) => {
        const numberElement = element.querySelector("[data-stat-number]");
        if (numberElement) {
          numberElement.textContent = `${element.dataset.statTarget}${element.dataset.statSuffix ?? ""}`;
        }
      });
      return;
    }

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );

    const statObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const element = entry.target as HTMLElement;
          const target = Number(element.dataset.statTarget ?? "0");
          const suffix = element.dataset.statSuffix ?? "";
          const numberElement = element.querySelector("[data-stat-number]");
          const startedAt = performance.now();
          const duration = 900;

          const tick = (now: number) => {
            const progress = Math.min((now - startedAt) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4);
            const value = Math.round(target * eased);
            if (numberElement) {
              numberElement.textContent = `${value >= 1000 ? value.toLocaleString() : value}${suffix}`;
            }
            if (progress < 1) {
              requestAnimationFrame(tick);
            }
          };

          requestAnimationFrame(tick);
          statObserver.unobserve(element);
        });
      },
      { threshold: 0.35 },
    );

    revealElements.forEach((element) => revealObserver.observe(element));
    statElements.forEach((element) => statObserver.observe(element));

    return () => {
      revealObserver.disconnect();
      statObserver.disconnect();
    };
  }, [rootRef]);
}

function LandingPage({ onStart }: { onStart: () => void }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useLandingEffects(rootRef);

  return (
    <div ref={rootRef} className="landing-page min-h-screen bg-[#FCFAF4] text-[#3A3530]">
      <LandingNav onStart={onStart} />
      <HeroSection onStart={onStart} />
      <StatsSection />
      <VideoSection />
      <ComparisonSection />
      {chapters.map((chapter) => (
        <ChapterSection key={chapter.number} chapter={chapter} onStart={onStart} />
      ))}
      <TimelineSection />
      <EmailExamplesSection />
      <ObjectionsSection />
      <FinalCta onStart={onStart} />
      <footer className="border-t border-[rgba(58,53,48,.12)] px-5 py-6 text-center text-sm text-[#5A5650] md:px-7">
        <div className="flex justify-center gap-6">
          <a href="/terms" className="hover:underline">
            Terms
          </a>
          <a href="/privacy" className="hover:underline">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
}

function LandingNav({ onStart }: { onStart: () => void }) {
  return (
    <nav className="sticky top-0 z-[100] flex items-center justify-between border-b border-[rgba(58,53,48,.12)] bg-[#FCFAF4]/[.92] backdrop-blur-md h-28 px-16 lg:pr-8">
      <BetterEmailLogo size="lg" />
      <div className="flex items-center gap-[12px]">
        <a href="/sign-in" className="hidden text-[13px] font-bold leading-none text-[#5A5650] transition hover:text-[#3A3530] sm:inline-flex">
          Sign in
        </a>
        <button
          type="button"
          onClick={onStart}
          className="flex items-stretch overflow-hidden rounded-full border-[1.5px] border-[#3A3530] bg-[#FCFAF4] shadow-xs cursor-pointer"
        >
          <span className="hidden items-center gap-[7px] py-[8.8px] pl-[18px] pr-[20px] text-[13px] font-bold leading-none tracking-[-.005em] text-[#3A3530] sm:flex">
            <span className="annotation relative -top-px text-[18px] font-bold italic leading-none text-[#B8832A] line-through">$2,500</span>
            <Row align tightGap>
              <span className="annotation relative -top-px text-[18px] font-bold italic leading-none text-[#B8832A]">Free</span>
              <span className="">enrichment</span>
            </Row>
            <span className="text-[#8A8278]">·</span>
            $99/mo
          </span>
          <span className="flex items-center rounded-full bg-[#3A3530] px-[18px] py-[10px] text-[12px] font-bold leading-none tracking-normal text-[#FCFAF4] sm:rounded-none sm:border-l-[1.5px] sm:border-[#3A3530] sm:px-[20px] sm:py-[8.8px] sm:text-[13px] sm:tracking-[.01em]">
            Get Launch Special →
          </span>
        </button>
      </div>
    </nav>
  );
}

function HeroSection({ onStart }: { onStart: () => void }) {
  return (
    <section className="mx-auto max-w-[980px] px-[20px] pb-[48px] pt-[32px] text-center md:px-[28px] md:pb-[56px] md:pt-[72px]">
      <p className="mx-auto text-left md:text-center mb-8 text-lg md:text-xl font-semibold uppercase tracking-[.18em] text-[#8A8278]">Email marketing for real estate agents</p>
      <h1
        aria-label="You have a database. We're great at email marketing. Let's work together"
        className="text-left md:text-center mb-[24px] max-w-full text-[42px] font-extrabold md:font-bold leading-[1.1] tracking-[-0.05em] text-[#3A3530] md:max-w-[920px] md:text-[64px] md:leading-[1.04] text-pretty py-8"
      >
        <span className="block">You have a database.</span>
        <span className="block mt-6 text-pretty">We're great at email marketing.</span>
        <ShinyText text="Let's work together" className="block mt-6" shineColorRgba="#8A827877" speed={3} /> <span className="inline-block" aria-hidden="true">🙂</span>
      </h1>
      <p className="mx-auto text-left md:text-center mb-[48px] max-w-[680px] text-[18px] font-medium leading-[1.7] text-[#5A5650] text-pretty">
        We enrich your database, personalize your strategy, write and send your campaigns, and monitor your sender reputation so emails land in the inbox. More deals. Zero tools to log into.
      </p>
      <div className="mx-auto flex max-w-[340px] flex-col items-stretch justify-center gap-[12px] md:max-w-none md:flex-row md:items-center md:gap-[14px]">
        <PrimaryCta onClick={onStart} className="w-full md:w-auto" />
        <SecondaryCta />
      </div>
      <p className="mt-[16px] text-[12px] text-[#8A8278]">$99/mo · No contracts · Cancel anytime</p>
      <CashStackVisual />
    </section>
  );
}

function CashStackVisual() {
  return (
    <div className="mx-auto mt-[36px] max-w-[680px] md:mt-[56px]">
      <svg
        className="h-auto w-full"
        viewBox="0 -36 580 356"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Comparison: your database earns far less than other agents who email theirs"
      >
        <path
          className="sk-draw"
          style={{ "--len": 140 } as React.CSSProperties}
          d="M 60 248 Q 110 250 175 247"
          stroke="#3A3530"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          opacity=".5"
        />
        <g className="sk-pop" style={{ "--r": "-2deg", animationDelay: "0ms" } as React.CSSProperties}>
          <path d="M 70 244 L 168 242 L 169 230 L 71 232 Z" fill="#FFFEF8" stroke="#3A3530" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="119" cy="237" r="4.5" fill="none" stroke="#3A3530" strokeWidth="1" />
          <text x="119" y="240" fontFamily="'Patrick Hand', cursive" fontSize="9" fontWeight="600" fill="#3A3530" textAnchor="middle">
            $
          </text>
        </g>
        <g className="sk-pop" style={{ "--r": "1.5deg", animationDelay: "120ms" } as React.CSSProperties}>
          <path d="M 68 232 L 170 230 L 171 218 L 69 220 Z" fill="#FFFEF8" stroke="#3A3530" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="119" cy="225" r="4.5" fill="none" stroke="#3A3530" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1deg", animationDelay: "240ms" } as React.CSSProperties}>
          <path d="M 72 220 L 167 218 L 168 206 L 73 208 Z" fill="#FFFEF8" stroke="#3A3530" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <text fontFamily="Inter, sans-serif" x="119" y="278" fontSize="13" fontWeight="600" textAnchor="middle" fill="#3A3530" letterSpacing=".05em">
          YOUR DATABASE
        </text>
        <text fontFamily="'Patrick Hand', cursive" x="119" y="302" fontSize="18" textAnchor="middle" fill="#A84030" fontStyle="italic">
          silent &amp; sleeping
        </text>

        <path
          className="sk-draw"
          style={{ "--len": 160 } as React.CSSProperties}
          d="M 360 248 Q 425 250 510 246"
          stroke="#3A3530"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          opacity=".5"
        />
        <g className="sk-pop" style={{ "--r": "-1deg", animationDelay: "80ms" } as React.CSSProperties}>
          <path d="M 372 244 L 502 242 L 503 230 L 373 232 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="237" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
          <text x="437" y="240" fontFamily="'Patrick Hand', cursive" fontSize="9" fontWeight="700" fill="#2C7A5A" textAnchor="middle">
            $
          </text>
        </g>
        <g className="sk-pop" style={{ "--r": "1.5deg", animationDelay: "120ms" } as React.CSSProperties}>
          <path d="M 370 232 L 504 230 L 505 218 L 371 220 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="225" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-2deg", animationDelay: "160ms" } as React.CSSProperties}>
          <path d="M 374 220 L 500 218 L 501 206 L 375 208 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1deg", animationDelay: "200ms" } as React.CSSProperties}>
          <path d="M 372 208 L 503 206 L 504 194 L 373 196 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="438" cy="201" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1.5deg", animationDelay: "240ms" } as React.CSSProperties}>
          <path d="M 370 196 L 501 194 L 502 182 L 371 184 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "2deg", animationDelay: "280ms" } as React.CSSProperties}>
          <path d="M 373 184 L 502 182 L 503 170 L 374 172 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="438" cy="177" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1deg", animationDelay: "320ms" } as React.CSSProperties}>
          <path d="M 371 172 L 503 170 L 504 158 L 372 160 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1.5deg", animationDelay: "360ms" } as React.CSSProperties}>
          <path d="M 374 160 L 500 158 L 501 146 L 375 148 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="153" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-2deg", animationDelay: "400ms" } as React.CSSProperties}>
          <path d="M 372 148 L 502 146 L 503 134 L 373 136 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1deg", animationDelay: "440ms" } as React.CSSProperties}>
          <path d="M 370 136 L 504 134 L 505 122 L 371 124 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="129" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1.5deg", animationDelay: "480ms" } as React.CSSProperties}>
          <path d="M 373 124 L 501 122 L 502 110 L 374 112 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "2deg", animationDelay: "520ms" } as React.CSSProperties}>
          <path d="M 371 112 L 503 110 L 504 98 L 372 100 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="105" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1deg", animationDelay: "560ms" } as React.CSSProperties}>
          <path d="M 374 100 L 502 98 L 503 86 L 375 88 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1.5deg", animationDelay: "600ms" } as React.CSSProperties}>
          <path d="M 372 88 L 504 86 L 505 74 L 373 76 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="438" cy="81" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-2deg", animationDelay: "640ms" } as React.CSSProperties}>
          <path d="M 370 76 L 501 74 L 502 62 L 371 64 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1deg", animationDelay: "680ms" } as React.CSSProperties}>
          <path d="M 373 64 L 503 62 L 504 50 L 374 52 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="438" cy="57" r="5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1.5deg", animationDelay: "720ms" } as React.CSSProperties}>
          <path d="M 371 52 L 502 50 L 503 38 L 372 40 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "2deg", animationDelay: "760ms" } as React.CSSProperties}>
          <path d="M 374 40 L 500 38 L 501 28 L 375 30 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="437" cy="34" r="4.5" fill="none" stroke="#2C7A5A" strokeWidth="1" />
        </g>
        <g className="sk-pop" style={{ "--r": "-1deg", animationDelay: "800ms" } as React.CSSProperties}>
          <path d="M 372 30 L 503 28 L 504 18 L 373 20 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1.5deg", animationDelay: "840ms" } as React.CSSProperties}>
          <path d="M 374 18 L 502 16 L 503 6 L 375 8 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "-2deg", animationDelay: "880ms" } as React.CSSProperties}>
          <path d="M 372 6 L 504 4 L 505 -6 L 373 -4 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "1deg", animationDelay: "920ms" } as React.CSSProperties}>
          <path d="M 371 -6 L 502 -8 L 503 -18 L 372 -16 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.6" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "8deg", animationDelay: "980ms" } as React.CSSProperties}>
          <path d="M 305 -22 L 360 -28 L 358 -36 L 304 -30 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.4" strokeLinejoin="round" />
        </g>
        <g className="sk-pop" style={{ "--r": "-12deg", animationDelay: "1020ms" } as React.CSSProperties}>
          <path d="M 510 -8 L 558 -16 L 555 -25 L 508 -17 Z" fill="#E8F4ED" stroke="#2C7A5A" strokeWidth="1.4" strokeLinejoin="round" />
        </g>

        <text fontFamily="Inter, sans-serif" x="437" y="278" fontSize="13" fontWeight="600" textAnchor="middle" fill="#3A3530" letterSpacing=".05em">
          OTHER AGENTS&apos; ACCOUNTS
        </text>
        <text fontFamily="'Patrick Hand', cursive" x="437" y="302" fontSize="18" textAnchor="middle" fill="#2C7A5A" fontStyle="italic" fontWeight="600">
          same database. emailed weekly.
        </text>

        <g className="sk-pop" style={{ "--r": "0deg", animationDelay: "1100ms" } as React.CSSProperties}>
          <ellipse cx="255" cy="115" rx="32" ry="22" fill="none" stroke="#B8832A" strokeWidth="1.8" transform="rotate(-4 255 115)" />
          <ellipse cx="256" cy="116" rx="28" ry="19" fill="none" stroke="#B8832A" strokeWidth="1.2" opacity=".55" transform="rotate(2 256 116)" />
          <text x="255" y="124" fontFamily="'Patrick Hand', cursive" fontSize="28" fontWeight="700" textAnchor="middle" fill="#B8832A">
            25×
          </text>
        </g>
        <path
          className="sk-draw"
          style={{ "--len": 120, animationDelay: "1.4s" } as React.CSSProperties}
          d="M 195 175 Q 250 130 320 145"
          stroke="#B8832A"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        <path
          className="sk-draw"
          style={{ "--len": 25, animationDelay: "1.9s" } as React.CSSProperties}
          d="M 320 145 L 308 138 M 320 145 L 311 154"
          stroke="#B8832A"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function StatsSection() {
  return (
    <section className="border-y border-[rgba(58,53,48,.12)] bg-[#FCFAF4]/60">
      <div className="mx-auto flex max-w-[980px] flex-wrap items-center justify-center gap-4 px-[20px] py-[24px] md:gap-0 md:px-0">
        {stats.map((stat) => (
          <div
            key={stat.label}
            data-stat-target={stat.value}
            data-stat-suffix={stat.suffix}
            className="border-r-0 border-[rgba(58,53,48,.12)] px-4 py-3 text-center md:border-r md:px-[48px] md:py-0 md:last:border-r-0"
          >
            <div className="text-[24px] font-bold leading-none tracking-[-.02em] text-[#3A3530]" data-stat-number>
              0{stat.suffix}
            </div>
            <div className="mt-1 text-[12px] font-medium tracking-[.04em] text-[#8A8278]">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function VideoSection() {
  return (
    <section id="video" data-reveal className="landing-reveal mx-auto max-w-[980px] scroll-mt-24 px-[20px] py-[32px] md:px-[28px] md:py-[56px]">
      <div className="relative overflow-hidden rounded-[6px] border-[1.5px] border-[#3A3530] bg-[#FCFAF4] shadow-[4px_5px_0_rgba(58,53,48,.06)]">
        <div className="absolute left-1/2 top-[-12px] z-20 h-[22px] w-[80px] -translate-x-1/2 rotate-[-2deg] rounded-[1px] border border-[rgba(120,100,60,.2)] bg-[rgba(232,221,180,.7)]" />
        <div className="relative aspect-video overflow-hidden bg-[#1A1815]">
          <iframe
            className="absolute inset-0 h-full w-full"
            src="https://www.loom.com/embed/bcd33611dc8c423eb1cae573af5865cf?hideEmbedTopBar=true&hide_title=true&hide_owner=true&hide_share=true&hide_speed=true"
            title="Better Email platform tour"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="flex items-center justify-between border-t-[1.5px] border-[#3A3530] bg-[#FCFAF4] px-[24px] py-[16px]">
          <span className="text-[14px] font-semibold text-[#3A3530]">Full platform tour · 5 min</span>
          <span className="text-[13px] text-[#8A8278]">Database enrichment to first send</span>
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  return (
    <section data-reveal className="landing-reveal mx-auto max-w-[980px] border-t border-[rgba(58,53,48,.12)] px-[20px] pb-[48px] pt-[48px] text-center md:px-[28px] md:pb-[68px] md:pt-[77px]">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-[.18em] text-[#8A8278]">One thing to know before you scroll</p>
      <h2 className="mx-auto mb-4 max-w-[720px] text-[30px] font-bold leading-[1.1] tracking-[-.025em] text-[#3A3530] md:text-[42px]">
        Better Email isn't <em className="italic">a tool you log into.</em>
      </h2>
      <p className="mx-auto mb-[56px] max-w-[560px] text-[17px] leading-[1.65] text-[#5A5650]">
        Most email platforms hand you a dashboard and call it a day. That's not what this is and the difference matters.
      </p>
      <div className="relative">
        <div className="grid gap-0 md:grid-cols-2 md:items-start md:gap-[48px]">
          <ComparisonCard label="what most platforms are" title="A tool you log into." items={comparisons} tone="bad" />
          <ComparisonCard label="what we are" title="A team that does it for you." items={betterEmailComparison} tone="good" />
        </div>
        <div className="comparison-vs annotation absolute left-1/2 top-[407px] z-10 flex h-[64px] w-[64px] items-center justify-center rounded-full border-2 border-[#B8832A] bg-[#FCFAF4] text-[28px] font-bold text-[#B8832A] shadow-[2px_3px_0_rgba(58,53,48,.06)] md:top-1/2 md:h-[80px] md:w-[80px] md:text-[36px]">
          vs.
        </div>
      </div>
    </section>
  );
}

function ComparisonCard({
  label,
  title,
  items,
  tone,
}: {
  label: string;
  title: string;
  items: string[];
  tone: "bad" | "good";
}) {
  return (
    <div
      className={cn(
        "relative rounded-[6px] border-[1.5px] border-[#3A3530] bg-[#FCFAF4] p-[32px] text-left shadow-[3px_4px_0_rgba(58,53,48,.08)]",
        tone === "bad" ? "comparison-card-bad mb-[56px] min-h-[383px] md:mb-0 md:min-h-[360px]" : "comparison-card-good min-h-[476px] md:min-h-[382px]",
      )}
    >
      <div
        className={cn(
          "absolute left-1/2 top-0 h-[22px] w-[80px] -translate-x-1/2 -translate-y-[14px] bg-[#E8DDB4]",
          tone === "bad" ? "rotate-[-3deg]" : "rotate-[2deg]",
        )}
      />
      <span
        className={cn(
          "comparison-card-tag annotation mb-5 inline-flex rounded-full border-[1.5px] px-[14px] py-1 text-[18px]",
          tone === "bad" ? "rotate-[-2deg] border-[#A84030] text-[#A84030]" : "rotate-[2deg] border-[#2C7A5A] text-[#2C7A5A]",
        )}
      >
        {label}
      </span>
      <h3
        className={cn(
          "mb-4 text-[24px] font-bold leading-[1.2] tracking-[-.015em] text-[#3A3530]",
          tone === "bad" && "line-through decoration-[#A84030] decoration-2",
        )}
      >
        {title}
      </h3>
      <p className="mb-6 text-[14px] leading-[1.65] text-[#5A5650]">
        {tone === "bad"
          ? 'Sits in your tabs. Goes unused. You blame yourself for not "being consistent." Cancelled in 90 days.'
          : 'We write the emails. We send them. We watch what works. The only thing you do is take the call when someone says "I\'m thinking of selling."'}
      </p>
      <ul className="space-y-3 text-[14px] leading-[1.5] text-[#5A5650]">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <ComparisonIcon tone={tone} />
            {item}
          </li>
        ))}
      </ul>
      {tone === "good" ? (
        <span className="comparison-note annotation mt-4 inline-block whitespace-nowrap text-[16px] text-[#B8832A] md:absolute md:bottom-[-36px] md:right-[-8px] md:mt-0 md:text-[18px]">
          ← this part
        </span>
      ) : null}
    </div>
  );
}

function ComparisonIcon({ tone }: { tone: "bad" | "good" }) {
  const color = tone === "bad" ? "#A84030" : "#2C7A5A";

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="mt-0.5 shrink-0">
      <circle cx="8" cy="8" r="6.5" stroke={color} strokeWidth="1.5" />
      {tone === "bad" ? (
        <path d="M5 5l6 6M11 5l-6 6" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      ) : (
        <path d="M4.5 8l2.5 2.5 4.5-5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function ChapterSection({
  chapter,
  onStart,
}: {
  chapter: (typeof chapters)[number];
  onStart: () => void;
}) {
  return (
    <section data-reveal className="landing-reveal border-t border-[rgba(58,53,48,.12)]">
      <div className="mx-auto flex w-full max-w-[980px] items-center gap-3 border-b border-[rgba(58,53,48,.12)] bg-[#F7F3E8]/40 px-[28px] py-[20px]">
        <span className="text-[12px] font-bold uppercase leading-[1.6] tracking-[.12em] text-[#B8832A]">#{chapter.number}. {chapter.title}</span>
        <span className="text-[12px] font-semibold uppercase leading-[1.6] tracking-[.12em] text-[#8A8278]">{chapter.kicker}</span>
      </div>
      <div className="mx-auto grid max-w-[980px] md:min-h-[819px] md:grid-cols-2">
        <div className="flex flex-col border-b border-[rgba(58,53,48,.12)] px-6 py-10 md:border-b-0 md:border-r md:px-10 md:py-[60px]">
          <h2 className="mb-8 max-w-[380px] text-[28px] font-bold leading-[1.18] tracking-[-.025em] text-[#3A3530] md:text-[34px]">
            {chapter.headline} <em className="italic text-[#B8832A]">{chapter.highlight}</em>
          </h2>
          <FeatureList items={chapter.items} />
          <blockquote className="annotation mt-7 rounded-r-[4px] border-l-[3px] border-[#B8832A] bg-[rgba(184,131,42,.06)] px-[22px] py-[18px] text-[20px] leading-[1.45] text-[#3A3530]">
            {chapter.note}
          </blockquote>
          <button type="button" onClick={onStart} className="mt-6 inline-flex items-center gap-1.5 self-start text-[13px] font-bold uppercase tracking-[.04em] text-[#2C7A5A]">
            {chapter.cta} →
          </button>
        </div>
        <ChapterMockup type={chapter.mockup} tilt={chapter.tilt} />
      </div>
    </section>
  );
}

function FeatureList({ items }: { items: (typeof chapters)[number]["items"] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="flex flex-1 flex-col">
      {items.map((item, index) => (
        <button
          key={item.title}
          type="button"
          onClick={() => setActiveIndex(index)}
          className="group relative block w-full border-t border-[rgba(58,53,48,.12)] py-5 pl-0 text-left first:border-t-0"
        >
          <span
            className={cn(
              "block text-[15px] font-semibold tracking-[.005em] transition",
              activeIndex === index ? "text-[#B8832A]" : "text-[#3A3530]",
            )}
          >
            {item.title}
          </span>
          <span
            className={cn(
              "block overflow-hidden text-[14px] leading-[1.65] text-[#5A5650] transition-all duration-300",
              activeIndex === index ? "mt-2 max-h-40 opacity-100" : "mt-0 max-h-0 opacity-0",
            )}
          >
            {item.body}
          </span>
        </button>
      ))}
    </div>
  );
}

function ChapterMockup({ type, tilt }: { type: string; tilt: string }) {
  return (
    <div className="relative flex min-h-[360px] items-center justify-center bg-[radial-gradient(ellipse_at_50%_50%,rgba(247,243,232,.5)_0%,transparent_70%)] px-6 py-8 md:min-h-[520px] md:px-10 md:py-12">
      <div
        className={cn(
          "relative w-full max-w-[420px] overflow-hidden rounded-[6px] border-[1.5px] border-[#3A3530] bg-[#FCFAF4] shadow-[4px_5px_0_rgba(58,53,48,.07)]",
          tilt === "left" ? "-rotate-1" : "rotate-1",
          type === "contacts" && "min-h-[321px]",
        )}
      >
        <span className="absolute left-1/2 top-0 z-10 h-5 w-20 -translate-x-1/2 -translate-y-1/2 rotate-[-2deg] bg-[#E8DDB4]" />
        <div className="flex items-center gap-1.5 border-b-[1.5px] border-[#3A3530] bg-[#F7F3E8] px-3.5 py-2">
          <span className="size-2 rounded-full border border-[#3A3530]" />
          <span className="size-2 rounded-full border border-[#3A3530]" />
          <span className="size-2 rounded-full border border-[#3A3530]" />
          <span className="ml-2 text-[10px] text-[#8A8278]">app.betteremail.com / {type === "contacts" ? "contacts" : type}</span>
        </div>
        {type === "contacts" ? <ContactsMockup /> : null}
        {type === "preview" ? <PreviewMockup /> : null}
        {type === "deliverability" ? <DeliverabilityMockup /> : null}
        {type === "onboarding" ? <OnboardingMockup /> : null}
      </div>
    </div>
  );
}

function ContactsMockup() {
  const contacts = [
    { initials: "MR", name: "Mike Rodriguez", meta: "Past client · 2018", value: "$847k", equity: "$312k equity" },
    { initials: "SL", name: "Sarah Lin", meta: "Sphere · 7 yrs", value: "$1.2M", equity: "$580k equity" },
    { initials: "JK", name: "James Kim", meta: "Enriching...", value: "", equity: "" },
  ];

  return (
    <div className="bg-[#FCFAF4] p-4">
      {contacts.map((contact) => (
        <div key={contact.initials} className="mb-2 flex items-center gap-2.5 rounded-sm border border-[rgba(58,53,48,.25)] bg-[#FCFAF4] px-3 py-2.5 last:mb-0">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#3A3530] bg-[#F7F3E8] text-xs font-bold text-[#3A3530]">
            {contact.initials}
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#3A3530]">{contact.name}</div>
            <div className="text-[11px] text-[#8A8278]">{contact.meta}</div>
          </div>
          {contact.value ? (
            <div className="text-right">
              <div className="annotation text-lg leading-none text-[#2C7A5A]">{contact.value}</div>
              <div className="text-[11px] text-[#8A8278]">{contact.equity}</div>
            </div>
          ) : null}
        </div>
      ))}
      <div className="annotation pt-2 text-center text-base text-[#B8832A]">2,847 contacts enriched</div>
    </div>
  );
}

function PreviewMockup() {
  return (
    <div className="bg-[#FCFAF4] p-4">
      <div className="mb-2.5 rounded-sm border border-[rgba(58,53,48,.25)] bg-[#FCFAF4] p-4">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[.1em] text-[#8A8278]">Subject line</div>
        <div className="mb-2.5 text-sm font-semibold text-[#3A3530]">Mike, your home has gained $94k since we closed</div>
        <div className="border-t border-[rgba(58,53,48,.12)] pt-2.5 text-xs leading-6 text-[#5A5650]">
          Hi Mike, I was looking at recent sales in your neighborhood and wanted to share something you'd want to know...
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <CheckPill label="Tone passed" />
        <CheckPill label="Data verified" />
      </div>
    </div>
  );
}

function DeliverabilityMockup() {
  return (
    <div className="bg-[#FCFAF4] p-4">
      <div className="mb-3 grid grid-cols-3 gap-2">
        <MetricBox value="98%" label="Inbox rate" />
        <MetricBox value="0.04" label="Bounce %" />
        <MetricBox value="High" label="Domain rep" />
      </div>
      <MetricRow label="IP warm-up" value="Day 18" width="78%" />
      <StatusRow label="DMARC" value="Passing" />
      <StatusRow label="SPF / DKIM" value="Authenticated" />
      <MetricRow label="List cleanliness" value="94%" width="94%" />
      <div className="annotation pt-2 text-center text-base text-[#B8832A]">all systems healthy</div>
    </div>
  );
}

function OnboardingMockup() {
  return (
    <div className="flex flex-col gap-2 bg-[#FCFAF4] p-4">
      <ChecklistRow label="kvCORE connected" done />
      <ChecklistRow label="2,847 contacts imported" done />
      <ChecklistRow label="4 segments built" done />
      <ChecklistRow label="First send scheduled" />
    </div>
  );
}

function CheckPill({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-sm border border-[#B8DCC8] bg-[#E8F4ED] px-3 py-2 text-xs font-semibold text-[#2C7A5A]">
      <CheckIcon />
      {label}
    </div>
  );
}

function MetricBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-sm border border-[rgba(58,53,48,.25)] bg-[#F7F3E8] p-3">
      <div className="text-xl font-bold leading-none text-[#3A3530]">{value}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-[.08em] text-[#8A8278]">{label}</div>
    </div>
  );
}

function MetricRow({ label, value, width }: { label: string; value: string; width: string }) {
  return (
    <div className="mb-1.5 flex items-center gap-2.5 rounded-sm border border-[rgba(58,53,48,.25)] bg-[#FCFAF4] px-3 py-2 text-xs">
      <span className="flex-1 font-medium text-[#5A5650]">{label}</span>
      <span className="h-1 flex-1 overflow-hidden rounded-full bg-[rgba(58,53,48,.12)]">
        <span className="block h-full rounded-full bg-[#2C7A5A]" style={{ width }} />
      </span>
      <span className="annotation min-w-9 text-right text-base leading-none text-[#2C7A5A]">{value}</span>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-1.5 flex items-center gap-2.5 rounded-sm border border-[rgba(58,53,48,.25)] bg-[#FCFAF4] px-3 py-2 text-xs">
      <span className="flex-1 font-medium text-[#5A5650]">{label}</span>
      <span className="rounded-sm border border-[#B8DCC8] bg-[#E8F4ED] px-2 py-1 text-[11px] font-semibold text-[#2C7A5A]">{value}</span>
    </div>
  );
}

function ChecklistRow({ label, done = false }: { label: string; done?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-sm px-3 py-3 text-sm",
        done
          ? "border border-[#B8DCC8] bg-[#E8F4ED] font-medium text-[#2C7A5A]"
          : "border border-dashed border-[rgba(58,53,48,.25)] bg-[#F7F3E8] text-[#8A8278]",
      )}
    >
      {done ? <CheckIcon /> : <span className="size-3 rounded-full border border-dashed border-[#8A8278]" />}
      {label}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="shrink-0">
      <circle cx="7" cy="7" r="6" stroke="#2C7A5A" strokeWidth="1.2" />
      <path d="M4.5 7l2 2 3-3" stroke="#2C7A5A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TimelineSection() {
  return (
    <section data-reveal className="landing-reveal border-t border-[rgba(58,53,48,.12)] px-[20px] py-[48px] text-center md:px-[28px] md:py-[64px]">
      <div className="mx-auto max-w-[720px]">
        <p className="mb-[16px] text-[11px] font-semibold uppercase tracking-[.18em] text-[#8A8278]">From signup to first email</p>
        <h2 className="mb-[12px] text-[30px] font-bold leading-[1.1] tracking-[-.025em] md:text-[42px]">
          Seven days. <em className="text-[#B8832A]">That's it.</em>
        </h2>
        <p className="mx-auto mb-[48px] max-w-[560px] text-[16px] leading-[1.7] text-[#5A5650]">
          Most platforms take three months to set up. Most agents quit before they finish. Here's what our seven days looks like.
        </p>
        <div className="relative mb-[-25px] h-[50px]">
          <svg viewBox="0 0 800 50" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" className="block h-[50px] w-full" aria-hidden="true">
            <path
              className="sketch-draw"
              style={{ "--len": 800 } as React.CSSProperties}
              d="M 80 25 Q 200 10 400 25 T 720 25"
              stroke="#B8832A"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="6 5"
            />
            <path
              className="sketch-draw delay-700"
              style={{ "--len": 30 } as React.CSSProperties}
              d="M 720 25 L 710 19 M 720 25 L 712 31"
              stroke="#B8832A"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="mt-[32px] grid gap-[24px] md:grid-cols-3 md:gap-[32px]">
          {timelineDays.map((day) => (
            <div
              key={day.day}
              className="timeline-day-card rounded-[6px] border-[1.5px] border-[#3A3530] bg-[#FCFAF4] px-[24px] py-[28px] text-left shadow-[3px_4px_0_rgba(58,53,48,.06)]"
            >
              <div className="annotation mb-[8px] text-[48px] font-bold leading-[.9] text-[#B8832A]">{day.day}</div>
              <h3 className="mb-[8px] text-[18px] font-bold tracking-[-.01em] text-[#3A3530]">{day.title}</h3>
              <p className="text-[14px] leading-[1.65] text-[#5A5650]">{day.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmailExamplesSection() {
  return (
    <section data-reveal className="landing-reveal border-t border-[rgba(58,53,48,.12)] px-[20px] py-[48px] text-center md:px-[28px] md:py-[64px]">
      <div className="mx-auto max-w-[980px]">
        <p className="mb-[16px] text-[11px] font-semibold uppercase tracking-[.18em] text-[#8A8278]">What lands in their inbox</p>
        <h2 className="mb-[12px] text-[30px] font-bold leading-[1.1] tracking-[-.025em] md:text-[42px]">
          Real emails. <em className="text-[#B8832A]">About them.</em>
        </h2>
        <p className="mx-auto mb-[48px] max-w-[560px] text-[16px] leading-[1.7] text-[#5A5650]">
          Three quick examples of the kinds of emails we send for you. Each one mentions something specific about the person — their address, their equity, what's selling on their street.
        </p>
        <div className="grid gap-[24px] md:grid-cols-3">
          {emailExamples.map((email) => (
            <div
              key={email.title}
              className="email-example-card relative rounded-[6px] border-[1.5px] border-[#3A3530] bg-[#FCFAF4] px-[20px] pb-[20px] pt-[24px] text-left shadow-[3px_4px_0_rgba(58,53,48,.06)]"
            >
              <div className="absolute left-1/2 top-[-12px] h-[18px] w-[64px] -translate-x-1/2 rotate-[-3deg] rounded-[1px] border border-[rgba(120,100,60,.18)] bg-[#E8DDB4]" />
              <p className="annotation mb-[6.4px] text-[15px] text-[#8A8278]">{email.note}</p>
              <h3 className="mb-[12px] border-b-[1.5px] border-[#3A3530] pb-[10px] text-[18px] font-bold leading-[1.25] tracking-[-.01em] text-[#3A3530]">{email.title}</h3>
              <p className="mb-[16px] text-[14px] italic leading-[1.6] text-[#5A5650]">{email.body}</p>
              <span className="email-example-tag annotation inline-block rounded-full border-[1.5px] border-[#B8832A] px-[12px] py-[3px] text-[15px] font-semibold not-italic text-[#B8832A]">
                {email.tag}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ObjectionsSection() {
  return (
    <section data-reveal className="landing-reveal border-t border-[rgba(58,53,48,.12)] px-[20px] py-[48px] text-center md:px-[28px] md:py-[64px]">
      <div className="mx-auto max-w-[980px]">
        <p className="mb-[16px] text-[11px] font-semibold uppercase tracking-[.18em] text-[#8A8278]">The questions everyone asks</p>
        <h2 className="mb-[12px] text-[30px] font-bold leading-[1.1] tracking-[-.025em] md:text-[42px]">
          Yeah. <em className="text-[#B8832A]">We thought of that too.</em>
        </h2>
        <p className="mx-auto mb-[48px] max-w-[560px] text-[16px] leading-[1.7] text-[#5A5650]">
          Three questions every agent asks us in the first ten minutes of the call. Here are the answers — so you don't have to ask.
        </p>
        <div className="grid gap-[24px] md:grid-cols-3 md:gap-[40px]">
          {objections.map((objection) => (
            <div key={objection.question} className="p-[8px] text-center">
              <p className="annotation mb-[16px] text-[24px] leading-[1.2] text-[#A84030]">&quot;{objection.question}&quot;</p>
              <svg className="mx-auto mb-[20px] mt-[16px] h-[50px] w-[60px]" viewBox="0 0 60 50" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  className="sketch-draw"
                  style={{ "--len": 60 } as React.CSSProperties}
                  d={objection.arrowPath}
                  stroke="#B8832A"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  className="sketch-draw delay-300"
                  style={{ "--len": 20 } as React.CSSProperties}
                  d="M 30 45 L 24 38 M 30 45 L 36 38"
                  stroke="#B8832A"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h3 className="mb-[12px] text-[18px] font-bold leading-[1.3] tracking-[-.01em] text-[#2C7A5A]">{objection.title}</h3>
              <p className="text-center text-[14px] leading-[1.65] text-[#5A5650]">{objection.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onStart }: { onStart: () => void }) {
  return (
    <section className="border-t border-[rgba(58,53,48,.12)] px-[20px] py-[56px] text-center md:px-[28px] md:py-[72px]">
      <h2 className="mx-auto mb-[12px] max-w-[680px] text-[32px] font-bold leading-[1.1] tracking-[-.03em] md:text-[44px]">
        Someone in your database is ready to move. They just need to{" "}
        <em className="text-[#B8832A]">hear from you first.</em>
      </h2>
      <p className="mx-auto mb-[32px] max-w-[480px] text-[16px] leading-[1.7] text-[#5A5650]">
        Book a 20-minute call. We'll show you who's in your database, what their homes are worth, and exactly what we'd say to them.
      </p>
      <PrimaryCta onClick={onStart} className="w-full md:w-auto" />
      <p className="mt-[16px] text-[11px] uppercase tracking-[.04em] text-[#8A8278]">$99/mo · No contracts · Cancel anytime</p>
      <div>
        <span className="final-cta-margin-note annotation mt-[16px] inline-block text-[20px] text-[#B8832A]">↑ takes 20 minutes. that's it.</span>
      </div>
    </section>
  );
}

const panelClass = "rounded-lg text-[#3A3530] p-8 pt-6 bg-white/50 border border-border shadow-xs";
const labelClass = "block text-[10px] font-bold uppercase tracking-[.12em] text-gray-500";

function FlowShell({
  step,
  onBack,
  showBack = true,
  children,
}: {
  step: FlowStep;
  onBack: () => void;
  showBack?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-[#FCFAF4] text-[#3A3530]">
      <nav className="flex items-center justify-between bg-white border-b border-[rgba(58,53,48,.12)] h-16 px-16 lg:pr-8">
        <BetterEmailLogo />
        {showBack ? (
          <Button size="sm" variant="ghost" className="text-sm text-[#8A8278] transition hover:text-[#3A3530]" onClick={onBack}>
            <Row align tightGap shrink>
              <ArrowLeft className="size-4 text-subdued-foreground" />
              <Text sm subdued>Back</Text>
            </Row>
          </Button>
        ) : (
          <span aria-hidden="true" />
        )}
      </nav>
      <div className={cn("mx-auto max-w-[480px] px-[32px] pb-12", step === "success" ? "pt-[28px]" : "pt-[48px]")}>
        {step !== "success" ? <Progress step={step} /> : null}
        {children}
      </div>
      <Column center noGap className="pb-12">
        <BetterEmailLogo />
        <Text sm subdued className="text-center">
          Copyright © 2026 Better Email. All rights reserved.
        </Text>
      </Column>
    </div>
  );
}

function Progress({ step }: { step: FlowStep }) {
  const activeIndex = step === "intake" ? 1 : step === "billing" ? 2 : 3;

  return (
    <Row align between className="w-full uppercase tracking-[.1em] text-gray-400 mb-6">
      <Column center tightGap>
        <ProgressPip activeIndex={activeIndex} index={1} />
        <Text sm className={cn(activeIndex === 1 && "text-gray-500")}>You</Text>
      </Column>
      <div className={cn("mt-11.5 rounded-full h-[1px] flex-1 bg-gray-300")} />
      <Column center tightGap>
        <ProgressPip activeIndex={activeIndex} index={2} />
        <Text sm className={cn(activeIndex === 2 && "text-gray-500")}>Billing</Text>
      </Column>
      <div className={cn("mt-11.5 rounded-full h-[1px] flex-1 bg-gray-300")} />
      <Column center tightGap>
        <ProgressPip activeIndex={activeIndex} index={3} />
        <Text sm className={cn(activeIndex === 3 && "text-gray-500")}>Connect</Text>
      </Column>
    </Row>
  );
}

function ProgressPip({ activeIndex, index }: { activeIndex: number; index: number }) {
  const done = activeIndex > index;
  const active = activeIndex === index;

  return (
    <div
      className={cn(
        "z-10 flex size-[28px] shrink-0 items-center justify-center rounded-full shadow-xs border-1 border-gray-300 pt-px text-gray-400/80",
        active && "border-teal-500 text-teal-500 bg-white",
        done && "border-teal-500 bg-teal-500 text-white",
      )}
    >
      {done ? <Check className="h-5 w-5 text-white flex-none" /> : <Text sm fat>{index}</Text>}
    </div>
  );
}

function IntakeStep({
  form,
  error,
  loginLinkSentEmail,
  submitting,
  onChange,
  onSubmit,
  onUseDifferentEmail,
}: {
  form: LeadFormState;
  error: string | null;
  loginLinkSentEmail: string | null;
  submitting: boolean;
  onChange: (updates: Partial<LeadFormState>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onUseDifferentEmail: () => void;
}) {
  if (loginLinkSentEmail) {
    return (
      <div className={panelClass}>
        <Column>
          <Row align between>
            <Text xs className="uppercase tracking-[.18em] text-gray-500">Onboarding</Text>
            <Text xs className="uppercase tracking-[.18em] text-gray-500">Secure login</Text>
          </Row>
          <Column center className="py-4 text-left">
            <Row align tightGap>
              <Text xxxxl chonky>
                Welcome
              </Text>
              <Text xxxxl className="text-[#5A5650] italic">
                back.
              </Text>
            </Row>
            <Text className="text-[#5A5650]">
              We found an existing Better Email account for {loginLinkSentEmail}.
            </Text>
            <Text className="text-[#5A5650]">
              We sent a secure login link so the account owner can continue. Check your inbox and click the link to continue.
            </Text>
          </Column>
          <Button variant="outline" className="w-full" type="button" onClick={onUseDifferentEmail}>
            Use a different email
          </Button>
        </Column>
      </div>
    );
  }

  return (
    <div className={panelClass}>
      <Column>
        <Row align between>
          <Text xs className="uppercase tracking-[.18em] text-gray-500">Onboarding</Text>
          <Text xs className="uppercase tracking-[.18em] text-gray-500">Step 1 of 3</Text>
        </Row>
        <Column noGap>
          <Text xxxxl chonky>
            Let's start
          </Text>
          <Text xxxxl className="text-[#5A5650] italic">
            with you.
          </Text>
        </Column>
        <Text className="text-[#5A5650]">We'll use this to set up your account and book your onboarding call.</Text>
        <form onSubmit={onSubmit} className="mt-2">
          <div className="mb-[14px]">
            <label className={labelClass} htmlFor="lead-name">
              Full name
            </label>
            <Input
              id="lead-name"
              type="text"
              value={form.name}
              onChange={(event) => onChange({ name: event.target.value })}
              placeholder="Sarah Johnson"
              required
            />
          </div>
          <div className="mb-[14px]">
            <label className={labelClass} htmlFor="lead-phone">
              Phone number
            </label>
            <Input
              id="lead-phone"
              type="tel"
              value={form.phone}
              onChange={(event) => {
                onChange({ phone: formatPhoneInput(event.target.value) });
              }}
              onBlur={(event) => {
                onChange({ phone: formatPhoneInput(event.target.value) });
              }}
              placeholder="(617) 555-0142"
              required
            />
          </div>
          <div className="mb-[14px]">
            <label className={labelClass} htmlFor="lead-email">
              Work email
            </label>
            <Input
              id="lead-email"
              type="email"
              value={form.email}
              onChange={(event) => onChange({ email: event.target.value })}
              placeholder="sarah@brokerage.com"
              required
            />
          </div>
          {error ? <div className="mb-3 rounded-sm bg-[#F8E8E2] px-3 py-2 text-sm text-[#A84030]">{error}</div> : null}
          <Button variant="black" className="mt-[12px] w-full" type="submit" disabled={submitting}>
            <Row align tightGap shrink>
              {submitting ? "Submitting..." : "Continue"}
              {submitting ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <ArrowRight className="h-4 w-4 text-white" />}
            </Row>
          </Button>
          <Row className="justify-center mt-4">
            <Row align tightGap shrink>
              <ShieldCheck className="h-4 w-4 text-[#8A8278]" />
              <Text className="text-center text-[12px] text-[#8A8278]">Your information is never shared or sold.</Text>
            </Row>
          </Row>
        </form>
      </Column>
    </div>
  );
}

function BillingPrice() {
  return (
    <Row end tightGap className="pt-4">
      <Text chonky className="text-[4rem] leading-none">$99</Text>
      <Text base className="text-[#5A5650]">/ month</Text>
    </Row>
  );
}

function BillingLaunchSpecialCallout() {
  return (
    <Column tightGap className="rounded-md border border-border p-4 mt-4 mb-8 shadow-xs">
      <Row align tightGap>
        <Check className="h-4 w-4 text-teal-500" />
        <Text sm fat>Launch Special included</Text>
      </Row>
      <Text sm className="text-[#5A5650]">
        Full CRM database enrichment with property data attached to every contact.
      </Text>
      <Row align tightGap shrink>
        <Text sm className="text-[#8A8278] line-through">$2,500 value</Text>
        <Row align tightGap shrink className="gap-1">
          <ArrowRight className="size-4 shrink-0 text-[#B8832A]" />
          <Text sm fat className="text-[#B8832A]">Cost covered by Better Email</Text>
        </Row>
      </Row>
    </Column>
  );
}

function BillingForm({
  clientSecret,
  flowState,
  fullName,
  completeBilling,
  onComplete,
}: {
  clientSecret: string;
  flowState: FlowState;
  fullName: string;
  completeBilling: CompleteBilling;
  onComplete: (result: CompleteBillingMetadata) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardName, setCardName] = useState(fullName);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCardComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError("Stripe has not loaded yet. Please wait a moment.");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card input is not ready yet. Please refresh and try again.");
      return;
    }

    if (!cardName.trim()) {
      setError("Please enter the name on the card.");
      return;
    }

    if (!isCardComplete) {
      setError("Please enter your card details.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: cardName.trim(),
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message || "We could not confirm the card. Please try again.");
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === "string" ? setupIntent.payment_method : setupIntent?.payment_method?.id;

      if (!paymentMethodId) {
        setError("Stripe did not return a payment method. Please try again.");
        return;
      }

      const result = await completeBilling({
        leadId: flowState.leadId,
        token: flowState.token,
        paymentMethodId,
      });

      onComplete(result);
    } catch (caughtError) {
      const message = getErrorMessage(caughtError, "We could not start your subscription. Please try again.");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <BillingPrice />
        <div className="mb-[14px] mt-8">
          <label className={labelClass} htmlFor="card-name">
            Name on card
          </label>
          <Input
            id="card-name"
            type="text"
            value={cardName}
            onChange={(event) => setCardName(event.target.value)}
            placeholder="Sarah Johnson"
            required
          />
        </div>
        <label className={labelClass}>Card details</label>
        <div className="mb-[4px] overflow-hidden rounded-md border border-border bg-card">
          <div className="flex min-h-[49px] items-center border-b border-border px-[14px] py-[12px]">
            <CardNumberElement
              className="min-h-[24px] w-full"
              options={{ style: cardElementStyle, showIcon: true, placeholder: "1234 1234 1234 1234" }}
              onChange={(event) => {
                setCardNumberComplete(event.complete);
                setError(event.error?.message ?? null);
              }}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="flex min-h-[49px] items-center border-r border-[rgba(58,53,48,.12)] px-[14px] py-[12px]">
              <CardExpiryElement
                className="min-h-[24px] w-full"
                options={{ style: cardElementStyle, placeholder: "MM / YY" }}
                onChange={(event) => {
                  setCardExpiryComplete(event.complete);
                  setError(event.error?.message ?? null);
                }}
              />
            </div>
            <div className="flex min-h-[49px] items-center px-[14px] py-[12px]">
              <CardCvcElement
                className="min-h-[24px] w-full"
                options={{ style: cardElementStyle, placeholder: "CVC" }}
                onChange={(event) => {
                  setCardCvcComplete(event.complete);
                  setError(event.error?.message ?? null);
                }}
              />
            </div>
          </div>
        </div>
        <div className="mb-[28px] flex items-start">
          <Row tightGap align>
            <LockKeyhole className="size-4 text-muted-foreground" />
            <Text sm subdued className="mt-px">Guaranteed secure checkout with Stripe</Text>
          </Row>
        </div>
        {error ? <Column tightGap className="-mt-6 mb-3 rounded-md border border-rose-700/20 shadow-xs bg-rose-50 px-3 py-2 text-sm text-rose-800"><Row align tightGap><AlertCircle className="size-4 shrink-0 text-rose-800" /><Text sm>{error}</Text></Row></Column> : null}
        <BillingLaunchSpecialCallout />
        <Button size="lg" variant="black" className="mt-[12px] w-full" type="submit" disabled={submitting}>
          <Row align tightGap shrink>
            <Text>{submitting ? "Reserving..." : "Reserve my spot"}</Text>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
          </Row>
        </Button>
      </form>
    </>
  );
}

function BillingStep({
  flowState,
  fullName,
  completeBilling,
  onBack,
  onNext,
  onComplete,
}: {
  flowState: FlowState | null;
  fullName: string;
  completeBilling: CompleteBilling;
  onBack: () => void;
  onNext: () => void;
  onComplete: (result: CompleteBillingMetadata) => void;
}) {

  const [billingMetadata, setBillingMetadata] = useState<BillingMetadata | null>(flowState?.billingMetadata ?? null);
  const [clientSecret, setClientSecret] = useState<string | null>(getBillingClientSecret(flowState?.billingMetadata));
  const [billingComplete, setBillingComplete] = useState(isBillingComplete(flowState?.billingMetadata));
  const [canShowBillingForm, setCanShowBillingForm] = useState(Boolean(getBillingClientSecret(flowState?.billingMetadata)));
  const [billingError, setBillingError] = useState<string | null>(null);
  const [preparingBilling, setPreparingBilling] = useState(!flowState?.billingMetadata);

  const prepareBilling = useAction(getStartedApi.prepareBilling);

  useEffect(() => {
    if (flowState?.billingMetadata) {
      setBillingMetadata(flowState.billingMetadata);
      setBillingError(null);
      setPreparingBilling(false);
      return;
    }

    let cancelled = false;

    async function run() {
      if (!flowState) return;

      setPreparingBilling(true);
      setBillingError(null);

      try {
        const result = await prepareBilling({
          leadId: flowState.leadId,
          token: flowState.token,
        });

        if (!cancelled) {
          setBillingMetadata(result);
        }
      } catch (caughtError) {
        if (!cancelled) {
          const message = getErrorMessage(caughtError, "We could not prepare billing. Please try again.");
          setBillingError(message);
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setPreparingBilling(false);
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [flowState?.leadId, flowState?.token, flowState?.billingMetadata, prepareBilling]);

  useEffect(() => {
    const _clientSecret = getBillingClientSecret(billingMetadata);
    const _billingComplete = isBillingComplete(billingMetadata);
    const _canShowBillingForm = Boolean(_clientSecret);
    setClientSecret(_clientSecret);
    setBillingComplete(_billingComplete);
    setCanShowBillingForm(_canShowBillingForm);
    setBillingError(getInactiveBillingMessage(billingMetadata));
  }, [billingMetadata]);

  const handleRetryBilling = useCallback(async () => {
    if (!flowState) return;
    setPreparingBilling(true);
    setBillingError(null);
    setBillingMetadata(null);
    try {
      const result = await prepareBilling({
        leadId: flowState.leadId,
        token: flowState.token,
      });
      setBillingMetadata(result);
    } catch (caughtError) {
      const message = getErrorMessage(caughtError, "We could not prepare billing. Please try again.");
      setBillingError(message);
      toast.error(message);
    } finally {
      setPreparingBilling(false);
    }
  }, [flowState, prepareBilling]);

  if (!flowState) {
    return (
      <div className={panelClass}>
        <Row align className="justify-center mt-8">
          <Loader2 className="size-6 animate-spin" />
        </Row>
      </div>
    );
  }

  let billingPanelContent = (<><Column noGap className="mb-6">
    <Text xxxxl chonky>
      Add billing
    </Text>
    <Text xxxxl className="text-[#5A5650] italic">
      details.
    </Text>
  </Column>
    <Column noGap>
      <Text base className="text-[#5A5650]">Start your membership today. First charge is $99.</Text>
      <Text base className="text-[#5A5650]">No contracts. Cancel anytime.</Text>
    </Column>

    {canShowBillingForm && clientSecret ? (
      <Elements stripe={stripePromise}>
        <BillingForm
          clientSecret={clientSecret}
          flowState={flowState}
          fullName={fullName}
          completeBilling={completeBilling}
          onComplete={onComplete}
        />
      </Elements>
    ) : billingError ? (
      <Column className="mt-8 rounded-md border border-rose-700/20 bg-rose-50 p-4 text-rose-800 shadow-xs">
        <Row align tightGap>
          <AlertCircle className="size-4 shrink-0 text-rose-800" />
          <Text sm>{billingError}</Text>
        </Row>
        <Button
          size="lg"
          variant="black"
          className="mt-2 w-full"
          type="button"
          onClick={() => {
            void handleRetryBilling();
          }}
          disabled={preparingBilling}
        >
          <Row align tightGap shrink>
            <Text>{preparingBilling ? "Preparing..." : "Try again"}</Text>
            {preparingBilling ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
          </Row>
        </Button>
      </Column>
    ) : (
      <Row align tightGap className="justify-center mt-8">
        <Loader2 className="size-6 animate-spin" />
        <Text sm subdued>Preparing secure billing</Text>
      </Row>
    )}

  </>);

  if (billingComplete) {
    billingPanelContent = (<Column>
      <Row align tightGap className="mt-2">
        <Check className="size-6 text-teal-500" />
        <Text xl fat>Billing is all set!</Text>
      </Row>
      <Text base>In the next step, we'll connect your CRM and prepare your database for onboarding.</Text>
      <Button size="lg" variant="black" className="mt-[12px] w-full" onClick={onNext}>
        <Row align tightGap shrink>
          <Text>Next</Text>
          <ArrowRight className="size-4" />
        </Row>
      </Button>
    </Column>
    );
  }

  return (
    <div className={panelClass}>
      <Row align between className="mb-4">
        <Button size="xs" variant="ghost" onClick={onBack} className="-ml-1.5">
          <ArrowLeft className="h-4 w-4" />
          <Text sm>Back</Text>
        </Button>
        <Text xs className="uppercase tracking-[.18em] text-gray-500">Step 2 of 3</Text>
      </Row>
      {billingPanelContent}
    </div>
  );
}

function ConnectStep({
  onBack,
  crm,
  onCrmSelected,
  onConnected,
}: {
  onBack: () => void;
  crm: string;
  onCrmSelected: (crm: string | null) => void;
  onConnected: () => void;
}) {

  const [selectedCrm, setSelectedCrm] = useState<string | null>(crm || null);
  const [crmName, setCrmName] = useState<string | null>(crm ? getCrmName(crm) : null);

  const crmButtonRef = useRef<{ reset: () => void }>(null);

  function handleCrmSelected(nextCrm: string | null) {
    setSelectedCrm(nextCrm);
    onCrmSelected(nextCrm);
  }

  const handleBack = () => {
    if (crmButtonRef.current && selectedCrm) {
      handleCrmSelected(null);
      crmButtonRef.current.reset();
    }
    else {
      onBack();
    }
  };

  useEffect(() => {
    const _crmName = getCrmName(selectedCrm);
    setCrmName(_crmName);
  }, [selectedCrm]);

  useEffect(() => {
    setSelectedCrm(crm || null);
  }, [crm]);

  return (
    <div className={panelClass}>
      <Row align between className="mb-6">
        <Button size="xs" variant="ghost" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
          <Text sm>Back</Text>
        </Button>
        <Text xs className="uppercase tracking-[.18em] text-gray-500">Step 3 of 3</Text>
      </Row>
      <Column tightGap className="mb-6">
        <Text xxxxl chonky>
          Connect your
        </Text>
        <Text xxxxl chonky className="text-[#5A5650] italic">
          {selectedCrm ? (crmName ?? selectedCrm) : "CRM"}
        </Text>
      </Column>
      {!selectedCrm ? (
        <p className="mb-[20px] text-[15px] leading-[1.7] text-[#5A5650]">
          <Text fat>Choose your CRM and follow the instructions to connect your account.</Text> We will have your database prepared before the onboarding call.
        </p>
      ) : null}
      <ConnectCrmButtons ref={crmButtonRef} initialAppKey={selectedCrm || null} compact onCrmSelected={handleCrmSelected} onConnected={onConnected} returnToRoute="/signup/success" hideChooseAnotherCrmButton={true} />
      <div className="mt-[18px] rounded-lg border border-border px-[16px] py-[14px] text-[13px] leading-[1.6] text-foreground shadow-xs">
        Having trouble connecting?{" "}
        <a href={getSecureCrmHref(selectedCrm ?? "")} className="font-semibold text-[#3A3530] underline">
          Send your CRM login or API key securely
        </a> and we will handle it for you.
      </div>
    </div>
  );
}

function SuccessStep() {
  return (
    <Column center className="pb-4 pt-2 text-center">
      <Column center>
        <Column center tightGap>
          <div className="h-18 w-18 rounded-full bg-teal-500 flex items-center justify-center pt-1">
            <Check className="size-12 text-white" />
          </div>
          <Text chonky className="text-[4rem]">
            You're in.
          </Text>
        </Column>

        <Column bigGap className="rounded-md border border-border bg-white/60 p-8 text-left shadow-xs">
          <Row start>
            <Check className="size-6 text-teal-500 shrink-0 mt-1.5" />
            <Text xl className="text-[#5A5650]">
              Your membership is confirmed
            </Text>
          </Row>
          <Row start>
            <Check className="size-6 text-teal-500 shrink-0 mt-1.5" />
            <Text xl className="text-[#5A5650] text-left">
              Better Email will cover the $2,500 database enrichment fee at no cost to you
            </Text>
          </Row>
          <Row start>
            <Check className="size-6 text-teal-500 shrink-0 mt-1.5" />
            <Text xl className="text-[#5A5650] text-left">
              Someone from our team will reach out within one business day
            </Text>
          </Row>
        </Column>
        <Row align tightGap className="mt-6 text-[#B8832A]  justify-center relative">
          <Text xxxxl chonky className="annotation tracking-[0.02em] italic">
            Here's what happens next
          </Text>
          <CurvedArrow className="size-20 absolute top-9 -right-3.5 rotate-150 text-gray-300 blur-[1px]" />
          <CurvedArrow className="size-20 absolute top-8 -right-3 rotate-150" />
        </Row>
      </Column>
      <Column wide noGap className=" rounded-md border border-border bg-white/60 text-left shadow-xs">
        <Column bigGap className="border-b border-border p-6">
          <Column tightGap>
            <Text xxl chonky className="text-foreground">
              Personalized onboarding
            </Text>
            <Text xl fat className="text-foreground">
              We will reach out to schedule a 1-on-1 call with you and your onboarding specialist.
            </Text>
          </Column>
          <Text lg className="text-foreground leading-relaxed">
            On the call, your specialist will ensure your CRM connection is working, show you around your newly-enriched database, and enable your first email automations.
          </Text>
        </Column>
        <Column tightGap className="px-6 py-5">
          <MetaItem>60 minutes, fully dedicated to your account</MetaItem>
          <MetaItem>Screen-share setup included</MetaItem>
          <MetaItem>No prep required from you</MetaItem>
        </Column>
      </Column>
      <Column wide noGap className="text-left">
        <TimelineItem
          when="Today"
          what="Membership confirmed"
          body="Your $2,500 enrichment is reserved at no cost."
          active
        />
        <TimelineItem
          when="Within 1 business day"
          what="Your specialist reaches out"
          body="We reach out to schedule your onboarding call."
        />
        <TimelineItem
          when="Onboarding call"
          what="CRM connected, database enriched"
          body="Your specialist handles everything live on the call."
        />
        <TimelineItem
          when="Within 7 days"
          what="First emails go live"
          body="Your emails are written, reviewed, and sent."
          last
        />
      </Column>
      <Column tightGap wide className="rounded-md border border-[#B8832A]/20 bg-[#B8832A]/10 px-5 py-4 text-left shadow-xs">
        <Text xxl fat className="annotation text-[#B8832A] tracking-[0.02em]">
          Your Launch Special is locked in
        </Text>
        <Text lg className="text-[#5A5650]">
          $2,500 database enrichment included free. We attach property data to your contacts before anything goes out.
        </Text>
      </Column>
      <Row align tightGap className="justify-center mt-12">
        <Text base>Need help?</Text> <Link href="mailto:support@trybetteremail.com?subject=Better Email Onboarding Support" className="text-primary hover:underline">Contact us</Link>
      </Row>
    </Column >
  );
}

function MetaItem({ children }: { children: React.ReactNode }) {
  return (
    <Row align tightGap shrink>
      <ArrowRight className="size-4" />
      <Text className="text-foreground">
        {children}
      </Text>
    </Row>
  );
}

function TimelineItem({ when, what, body, active, last }: { when: string; what: string; body: string; active?: boolean; last?: boolean }) {
  return (
    <Row start noGap className="relative py-4">
      <Column noGap center className="relative mr-4 w-5 shrink-0">
        {!last ? <span className="absolute bottom-[-90px] top-7 w-px bg-[rgba(58,53,48,.12)]" aria-hidden="true" /> : null}
        <Row
          align
          noGap
          shrink
          className={cn(
            "mt-2 relative z-10 size-5 justify-center rounded-full border bg-[#FCFAF4]",
            active ? "border-[#2C7A5A] bg-[#2C7A5A]" : "border-[rgba(58,53,48,.25)]",
          )}
        >
          {active ? <span className="size-2 rounded-full bg-teal-100" /> : null}
        </Row>
      </Column>
      <Column noGap className="flex-1 gap-1">
        <Text xxl className="annotation text-[#2C7A5A] tracking-[0.02em]">
          {when}
        </Text>
        <Text xl className="text-foreground">
          {what}
        </Text>
        <Text lg className="text-foreground/80">
          {body}
        </Text>
      </Column>
    </Row>
  );
}

export default function HomePage() {
  const { isLoading, isAuthenticated } = useBetterEmailAuth();
  const { signIn } = useAuthActions();
  const router = useRouter();
  const startFlow = useAction(getStartedApi.startFlow);
  const prepareBilling = useAction(getStartedApi.prepareBilling);
  const completeBilling = useAction(getStartedApi.completeBilling);
  const [mode, setMode] = useState<"landing" | "flow">("landing");
  const [step, setStep] = useState<FlowStep>("intake");
  const [form, setForm] = useState<LeadFormState>(DEFAULT_LEAD_FORM);
  const [flowState, setFlowState] = useState<FlowState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [loginLinkSentEmail, setLoginLinkSentEmail] = useState<string | null>(null);
  const urlStep = getSignupStepFromUrl();
  const isSuccessUrl = urlStep === "success";
  const shouldRenderSignupFlow = mode === "flow" && Boolean(urlStep) && (Boolean(flowState) || isSuccessUrl);

  const goToFlowStep = useCallback((nextStep: FlowStep, options: { replace?: boolean; scroll?: boolean } = {}) => {
    setMode("flow");
    setStep(nextStep);
    updateSignupUrl(nextStep, options.replace ? "replace" : "push");
    if (options.scroll ?? true) {
      scrollToTop();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const urlStep = getSignupStateFromUrl()?.step ?? null;
      const stored = readSignupStorage();
      if (urlStep === "success") {
        return;
      }
      if (urlStep && (flowState || stored?.flowState)) {
        return;
      }
      router.push("/v2");
    }
  }, [flowState, isAuthenticated, isLoading, router]);

  useEffect(() => {
    const stored = readSignupStorage();
    const urlState = getSignupStateFromUrl();
    const urlStep = urlState?.step ?? null;

    if (stored) {
      setForm({
        ...stored.form,
        ...(urlState?.step === "connect" && urlState.crm && stored.flowState ? { crm: urlState.crm } : {}),
      });
      setFlowState(stored.flowState);
    }

    if (!urlStep) {
      return;
    }

    if (urlStep !== "intake" && urlStep !== "success" && !stored?.flowState) {
      setMode("flow");
      setStep("intake");
      updateSignupUrl("intake", "replace");
      return;
    }

    setMode("flow");
    setStep(urlStep);
  }, []);

  useEffect(() => {
    if (mode !== "flow" && !flowState && !hasLeadFormValue(form)) {
      return;
    }

    writeSignupStorage(form, flowState);
  }, [flowState, form, mode]);

  useEffect(() => {
    const handlePopState = () => {
      const urlState = getSignupStateFromUrl();
      const urlStep = urlState?.step ?? null;
      if (!urlStep) {
        setMode("landing");
        setStep("intake");
        return;
      }

      if (urlStep !== "intake" && urlStep !== "success" && !flowState) {
        setMode("flow");
        setStep("intake");
        updateSignupUrl("intake", "replace");
        return;
      }

      setMode("flow");
      setStep(urlStep);
      if (urlState?.step === "connect" && urlState.crm) {
        setForm((current) => ({ ...current, crm: urlState.crm ?? "" }));
      }
      scrollToTop();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [flowState]);

  const openFlow = () => {
    setIntakeError(null);
    setLoginLinkSentEmail(null);
    goToFlowStep("intake");
  };

  const showLanding = () => {
    setMode("landing");
    setStep("intake");
    setIntakeError(null);
    setLoginLinkSentEmail(null);
    clearSignupUrl("push");
    scrollToTop();
  };

  const useDifferentEmail = () => {
    setIntakeError(null);
    setLoginLinkSentEmail(null);
    setFlowState(null);
    setForm((current) => {
      const nextForm = { ...current, email: "" };
      writeSignupStorage(nextForm, null);
      return nextForm;
    });
  };

  const handleIntakeSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitting(true);
    setIntakeError(null);
    setLoginLinkSentEmail(null);

    try {
      const result = await startFlow({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        crm: form.crm || "",
        redirectTo: getCurrentSignupRedirectPath(),
      });

      if (result.status === "loginLinkSent") {
        setFlowState(null);
        setLoginLinkSentEmail(form.email.trim());
        writeSignupStorage(form, null);
        return;
      }

      const nextFlowState: FlowState = {
        leadId: result.leadId,
        teamId: result.teamId,
        userId: result.userId,
        token: result.token,
        authToken: result.authToken,
        billingMetadata: result.billingMetadata,
      };
      setFlowState(nextFlowState);
      writeSignupStorage(form, nextFlowState);
      await signIn("magic-link", {
        token: nextFlowState.authToken,
      });
      goToFlowStep("billing");
    } catch (caughtError) {
      const message = getErrorMessage(caughtError, "We could not submit your details. Please try again.");
      setIntakeError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || (isAuthenticated && !shouldRenderSignupFlow)) {
    return null;
  }

  return (
    <>
      <link href={LANDING_FONT_HREF} rel="stylesheet" />
      {mode === "landing" ? (
        <LandingPage onStart={openFlow} />
      ) : (
        <FlowShell step={step} onBack={showLanding} showBack={step === "intake" || step === "billing"}>
          {step === "intake" ? (
            <IntakeStep
              form={form}
              error={intakeError}
              loginLinkSentEmail={loginLinkSentEmail}
              submitting={submitting}
              onChange={(updates) => {
                setLoginLinkSentEmail(null);
                setForm((current) => ({ ...current, ...updates }));
              }}
              onSubmit={handleIntakeSubmit}
              onUseDifferentEmail={useDifferentEmail}
            />
          ) : step === "billing" ? (
            <BillingStep
              flowState={flowState}
              fullName={form.name}
              completeBilling={completeBilling}
              onBack={() => goToFlowStep("intake")}
              onNext={() => goToFlowStep("connect")}
              onComplete={(result) => {
                setFlowState((current) => current ? { ...current, billingMetadata: result } : current);
              }}
            />
          ) : step === "connect" ? (
            <ConnectStep
              onBack={() => goToFlowStep("billing")}
              crm={form.crm}
              onCrmSelected={(crm) => setForm((current) => ({ ...current, crm: crm ?? "" }))}
              onConnected={() => {
                goToFlowStep("success");
              }}
            />
          ) : (
            <SuccessStep />
          )}
        </FlowShell>
      )}
    </>
  );
}
